import React, { useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { useDrag, useDrop } from 'react-dnd';
import { getEmptyImage } from 'react-dnd-html5-backend';
import { Icon } from '@Sage/hoi-poi-ui';

import { DOCUMENTS } from 'constants/Entities';
import { EntityListActions, DocumentActions } from 'actions';
import { ENTITY_LIST_DRAG_DROP, TREE_NODE_DRAG_DROP } from 'constants/Constants';
import colors from 'constants/colors';
import TruncatedTooltip from 'components/TruncatedTooltip';

const propTypes = {
    node_id: PropTypes.string,
    childLength: PropTypes.number,
    selected: PropTypes.bool,
    currentNode: PropTypes.object,
    dataSource: PropTypes.object,
    entityType: PropTypes.object,
    isOpen: PropTypes.bool,
    depth: PropTypes.number,
    onClick: PropTypes.func,
    onArrowClick: PropTypes.func,
};

//Check if dragged is parent of drop receiver.
const checkIfCanDrop = (tree, draggedRow, currentNode) => {
    if (currentNode.id === '-1' && draggedRow.node_id !== '-1') {
        return true;
    } else if (tree[currentNode.node_id].folder.id === draggedRow.id) {
        return false;
    } else if (tree[currentNode.node_id].folder.id === '-1') {
        return true;
    } else {
        return checkIfCanDrop(tree, draggedRow, tree[currentNode.node_id].folder);
    }
};

const mapStateToProps = (state, ownProps) => {
    const draggedRow =
        state.entityList[ownProps.entityType.entity] &&
        state.entityList[ownProps.entityType.entity].draggedRow
            ? state.entityList[ownProps.entityType.entity].draggedRow
            : null;
    const permission = state.config.permission;
    const folderPermission = permission.crud_permission.DOCUMENTLIBRARY;

    return {
        draggedRow,
        canEditFolder: permission.documentos_manager && folderPermission.update,
    };
};

const mapDispatchToProps = (dispatch) => {
    return {
        setDraggedRow: bindActionCreators(EntityListActions, dispatch).setDraggedRow,
        updateEntity: bindActionCreators(DocumentActions, dispatch).updateEntity,
    };
};

const TreeRowDragAndDrop = ({
    node_id,
    childLength,
    selected,
    colorIconSelected,
    dataSource,
    isOpen,
    depth,
    onClick,
    onArrowClick,
    currentNode,
    draggedRow,
    canEditFolder,
    entityType,
    setDraggedRow,
    updateEntity,
    iconType,
}) => {
    const ref = useRef(null);

    const [{ isDragging }, connectDragSource, connectDragPreview] = useDrag({
        type: TREE_NODE_DRAG_DROP.TREE_NODE,
        item: () => {
            setDraggedRow(entityType.entity, currentNode);
            return {
                draggedRow: currentNode,
            };
        },
        end: (item, monitor) => {
            setDraggedRow(entityType.entity);

            switch (entityType) {
                //Add cases if necessary
                case DOCUMENTS:
                    if (monitor.getDropResult()) {
                        const targetRow = monitor.getDropResult().targetRow;
                        const draggedRow = item.draggedRow;
                        const updatedRow = {
                            ...draggedRow,
                            node_id: targetRow.id,
                        };

                        updateEntity(
                            updatedRow,
                            updatedRow.description,
                            updatedRow.level,
                            '',
                            true,
                        );
                    }
            }
        },
        canDrag: () => {
            if (currentNode.id === '-1' || !canEditFolder) return false;
            return true;
        },
        collect: (monitor) => ({
            isDragging: monitor.isDragging(),
        }),
    });

    const [{ isOverTreeNode, canDropTreeNode }, connectDropTargetTreeNode] = useDrop({
        accept: TREE_NODE_DRAG_DROP.TREE_NODE,
        drop: () => {
            return {
                targetRow: currentNode,
            };
        },
        canDrop: (item, monitor) => {
            const draggedRow = item.draggedRow;

            if (!monitor.isOver()) return false;
            if (draggedRow.id === '-1') return false;
            if (draggedRow.node_id === '-1' && !currentNode.node_id) return false;
            if (draggedRow.id === currentNode.id) return false;
            if (draggedRow.node_id === currentNode.id) return false;

            let canItDrop = checkIfCanDrop(dataSource, draggedRow, currentNode);

            if (!canItDrop) return false;

            return true;
        },
        collect: (monitor) => ({
            isOverTreeNode: monitor.isOver(),
            canDropTreeNode: monitor.canDrop(),
        }),
    });

    const [{ isOverEntityList, canDropEntityList }, connectDropTargetEntityList] = useDrop({
        accept: ENTITY_LIST_DRAG_DROP.DOCUMENTS_ROW,
        drop: () => {
            return {
                targetRow: currentNode,
            };
        },
        canDrop: (item, monitor) => {
            const draggedRow = item.draggedRow;

            if (!monitor.isOver()) return false;
            if (draggedRow.id === '-1') return false;
            if (draggedRow.node_id === '-1' && !currentNode.node_id) return false;
            if (draggedRow.id === currentNode.id) return false;
            if (draggedRow.node_id === currentNode.id) return false;

            let canItDrop = checkIfCanDrop(dataSource, draggedRow, currentNode);

            if (!canItDrop) return false;

            return true;
        },
        collect: (monitor) => ({
            isOverEntityList: monitor.isOver(),
            canDropEntityList: monitor.canDrop(),
        }),
    });

    useEffect(() => {
        connectDragPreview(getEmptyImage(), {
            captureDraggingState: true,
        });
    }, [connectDragPreview]);

    // Combine all drop refs
    connectDropTargetTreeNode(ref);
    connectDropTargetEntityList(ref);
    connectDragSource(ref);

    const handleArrowClick = useCallback(
        (event) => {
            event.stopPropagation();
            event.preventDefault();
            onArrowClick();
        },
        [onArrowClick],
    );

    const renderIcon = useCallback(() => {
        const types = {
            folder: (
                <Icon
                    name={isOpen ? 'folderOpen' : 'folder'}
                    size="large"
                    color={colors['var(--grey-500)']}
                />
            ),
            //ADD CATEGORIES IF NECESSARY
        };

        return <span>{types[iconType]}</span>;
    }, [iconType, isOpen]);

    let node = dataSource[node_id].folder;

    const isDragAllowed =
        (isOverEntityList || isOverTreeNode) && (canDropEntityList || canDropTreeNode);

    let colorSelected = '';
    if (selected && colorIconSelected) colorSelected = colorIconSelected;

    let classNameItem = ['fm-tree-navigator-item'];
    if (selected) classNameItem.push('selected');
    if (colorSelected) classNameItem.push(colorSelected);
    if (node_id === '-1') classNameItem.push('is-root');
    if (node_id !== '-1') classNameItem.push('not-root');
    if (childLength === 0) classNameItem.push('no-children');
    if (isOpen) classNameItem.push('is-open');

    if (isDragAllowed && !selected) classNameItem.push('drag-allowed');
    else if (isDragAllowed && selected) classNameItem.push('drag-allowed-selected');

    if (draggedRow && currentNode && draggedRow.id === currentNode.id)
        classNameItem.push('dragged-row');

    const classNameIcon = [
        'fm-tree-navigator-icon',
        currentNode.id !== '-1' && canEditFolder ? 'draggable' : '',
    ].join(' ');

    return (
        <div
            ref={ref}
            className={classNameItem.join(' ')}
            style={{ marginLeft: depth * (node_id === '-1' ? 10 : 20) }}
            onClick={onClick}
        >
            <div className="fm-tree-navigator-arrow-container" onClick={handleArrowClick}>
                <div className="fm-tree-navigator-arrow">
                    <Icon name="arrowDropDownRaw" size="raw" color="currentColor" />
                </div>
            </div>
            <div className={classNameIcon}>{renderIcon()}</div>
            <div className="fm-tree-navigator-text">
                <TruncatedTooltip content={node.name}>{node.name}</TruncatedTooltip>
            </div>
        </div>
    );
};

TreeRowDragAndDrop.propTypes = propTypes;

export default connect(mapStateToProps, mapDispatchToProps)(TreeRowDragAndDrop);
